import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { activeStyle } from "../styles";

// Geometry helpers the rider's builders share (#114).

/**
 * A round piece's segment count in the active style's rider: `n` scaled by
 * `RiderBuild.facets` (1 keeps it), never under `min` (#76, retro's low-poly rider).
 */
export function facets(n: number, min = 3): number {
  const f = activeStyle().rider.facets;
  return f === 1 ? n : Math.max(min, Math.round(n * f));
}

/** Smoothstep on [0, 1]: 0 below, 1 above, eased in between. */
export function smooth(t: number): number {
  const c = Math.min(1, Math.max(0, t));
  return c * c * (3 - 2 * c);
}

/** Smoothstep from `e0` to `e1` (either way round): 0 at e0, 1 at e1. */
export function smoothstep(e0: number, e1: number, v: number): number {
  return smooth((v - e0) / (e1 - e0));
}

/**
 * Lathe seams: the first and last columns sit on each other, but after a
 * deform `computeVertexNormals` sees them as two edges. Average them.
 * `points` is the lathe profile's length and `segments` its segment count.
 */
export function closeSeam(geo: THREE.BufferGeometry, points: number, segments: number): void {
  geo.computeVertexNormals();
  const n = geo.getAttribute("normal");
  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  for (let j = 0; j < points; j++) {
    const last = segments * points + j;
    a.fromBufferAttribute(n, j);
    b.fromBufferAttribute(n, last);
    a.add(b).normalize();
    n.setXYZ(j, a.x, a.y, a.z);
    n.setXYZ(last, a.x, a.y, a.z);
  }
}

/**
 * Merges parts into one geometry, non-indexed with only position, normal
 * and uv, so parts built differently still merge. Disposes the parts.
 */
export function mergeFlat(parts: THREE.BufferGeometry[]): THREE.BufferGeometry {
  const flat = parts.map((g) => {
    const f = g.index ? g.toNonIndexed() : g;
    for (const name of Object.keys(f.attributes)) if (name !== "position" && name !== "normal" && name !== "uv") f.deleteAttribute(name);
    return f;
  });
  const merged = mergeGeometries(flat)!;
  for (const g of new Set([...parts, ...flat])) g.dispose();
  return merged;
}

/**
 * The part of a mesh where `keep(p)` >= 0, cut along that level: each
 * triangle the level crosses is trimmed there, with its normals and UVs
 * interpolated, so a smooth `keep` gives a smooth edge on a fine enough mesh.
 * `keep` runs once per vertex. Non-indexed, position, normal and uv.
 */
export function clipTo(geo: THREE.BufferGeometry, keep: (p: THREE.Vector3) => number): THREE.BufferGeometry {
  const P = geo.getAttribute("position").array;
  const N = geo.getAttribute("normal").array;
  const U = geo.getAttribute("uv").array;
  const count = P.length / 3;
  const f = new Float32Array(count);
  const p = new THREE.Vector3();
  for (let i = 0; i < count; i++) f[i] = keep(p.set(P[i * 3]!, P[i * 3 + 1]!, P[i * 3 + 2]!));
  const index = geo.index?.array;
  const tris = index ? index.length : count;
  const pos: number[] = [];
  const nor: number[] = [];
  const uv: number[] = [];
  // A point on the edge from vertex i to j, t of the way along.
  const emit = (i: number, j: number, t: number) => {
    for (let k = 0; k < 3; k++) {
      pos.push(P[i * 3 + k]! + (P[j * 3 + k]! - P[i * 3 + k]!) * t);
      nor.push(N[i * 3 + k]! + (N[j * 3 + k]! - N[i * 3 + k]!) * t);
    }
    for (let k = 0; k < 2; k++) uv.push(U[i * 2 + k]! + (U[j * 2 + k]! - U[i * 2 + k]!) * t);
  };
  const vi = [0, 0, 0];
  const poly: [number, number, number][] = [];
  for (let a = 0; a < tris; a += 3) {
    for (let k = 0; k < 3; k++) vi[k] = index ? index[a + k]! : a + k;
    const [f0, f1, f2] = [f[vi[0]!]!, f[vi[1]!]!, f[vi[2]!]!];
    if (f0 >= 0 && f1 >= 0 && f2 >= 0) {
      for (let k = 0; k < 3; k++) emit(vi[k]!, vi[k]!, 0);
      continue;
    }
    if (f0 < 0 && f1 < 0 && f2 < 0) continue;
    // Walk the triangle's edges, keeping inside corners and adding a point where an edge crosses the level.
    poly.length = 0;
    for (let k = 0; k < 3; k++) {
      const i = vi[k]!;
      const j = vi[(k + 1) % 3]!;
      if (f[i]! >= 0) poly.push([i, i, 0]);
      if (f[i]! >= 0 !== f[j]! >= 0) poly.push([i, j, f[i]! / (f[i]! - f[j]!)]);
    }
    for (let k = 1; k + 1 < poly.length; k++) for (const v of [poly[0]!, poly[k]!, poly[k + 1]!]) emit(...v);
  }
  const out = new THREE.BufferGeometry();
  out.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  out.setAttribute("normal", new THREE.Float32BufferAttribute(nor, 3));
  out.setAttribute("uv", new THREE.Float32BufferAttribute(uv, 2));
  return out;
}

const shapes = new Map<string, THREE.BufferGeometry>();

/**
 * A copy of the shape `build` makes for `key`, built once: for costly
 * shapes that only depend on a few numbers (a tank top cut to a width).
 * The copy is the caller's to dispose; the kept original never is.
 */
export function cachedShape(key: string, build: () => THREE.BufferGeometry): THREE.BufferGeometry {
  let shape = shapes.get(key);
  if (!shape) {
    shape = build();
    shapes.set(key, shape);
  }
  return shape.clone();
}
